<script>
import { GlSprintf, GlFormRadioGroup, GlFormRadio } from '@gitlab/ui';
import { s__ } from '~/locale';
import { DUO_PRO, DUO_ENTERPRISE } from 'ee/usage_quotas/code_suggestions/constants';
import CascadingLockIcon from '~/namespaces/cascading_settings/components/cascading_lock_icon.vue';
import { AVAILABILITY_OPTIONS } from '../constants';

export default {
  name: 'DuoAvailabilityForm',
  availabilityOptions: {
    defaultOn: AVAILABILITY_OPTIONS.DEFAULT_ON,
    defaultOff: AVAILABILITY_OPTIONS.DEFAULT_OFF,
    alwaysOff: AVAILABILITY_OPTIONS.NEVER_ON,
  },

  i18n: {
    sectionTitle: {
      [DUO_PRO]: s__('AiPowered|GitLab Duo Pro availability'),
      [DUO_ENTERPRISE]: s__('AiPowered|GitLab Duo Enterprise availability'),
      default: s__('AiPowered|Availability'),
    },
    defaultOnText: s__('AiPowered|On by default'),
    defaultOnHelpText: s__(
      'AiPowered|Features are available. However, any group, subgroup, or project can turn them off.',
    ),
    defaultOffText: s__('AiPowered|Off by default'),
    defaultOffHelpText: s__(
      'AiPowered|Features are not available. However, any group, subgroup, or project can turn them on.',
    ),
    alwaysOffText: s__('AiPowered|Always off'),
    alwaysOffHelpText: s__(
      'AiPowered|Features are not available and cannot be turned on for any group, subgroup, or project.',
    ),
  },
  components: {
    GlSprintf,
    GlFormRadioGroup,
    GlFormRadio,
    CascadingLockIcon,
  },
  inject: {
    areDuoSettingsLocked: { required: true },
    cascadingSettingsData: { required: true },
    duoProOrDuoEnterpriseTier: { default: null },
  },
  props: {
    duoAvailability: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      duoAvailabilityState: this.duoAvailability,
    };
  },
  computed: {
    title() {
      return (
        this.$options.i18n.sectionTitle[this.duoProOrDuoEnterpriseTier] ||
        this.$options.i18n.sectionTitle.default
      );
    },
    showCascadingButton() {
      return (
        this.areDuoSettingsLocked &&
        this.cascadingSettingsData &&
        Object.keys(this.cascadingSettingsData).length
      );
    },
  },

  methods: {
    radioChanged() {
      this.$emit('change', this.duoAvailabilityState);
    },
  },
};
</script>
<template>
  <div>
    <h5>{{ title }}</h5>
    <gl-form-radio-group v-model="duoAvailabilityState">
      <gl-form-radio
        :value="$options.availabilityOptions.defaultOn"
        :disabled="areDuoSettingsLocked"
        @change="radioChanged"
      >
        {{ $options.i18n.defaultOnText }}

        <template #help>
          <gl-sprintf :message="$options.i18n.defaultOnHelpText" />
        </template>
      </gl-form-radio>
      <slot name="amazon-q-settings"></slot>

      <gl-form-radio
        :value="$options.availabilityOptions.defaultOff"
        :disabled="areDuoSettingsLocked"
        @change="radioChanged"
      >
        {{ $options.i18n.defaultOffText }}
        <template #help>
          <gl-sprintf :message="$options.i18n.defaultOffHelpText" />
        </template>
      </gl-form-radio>
      <gl-form-radio
        :value="$options.availabilityOptions.alwaysOff"
        :disabled="areDuoSettingsLocked"
        @change="radioChanged"
      >
        {{ $options.i18n.alwaysOffText }}
        <cascading-lock-icon
          v-if="showCascadingButton"
          :is-locked-by-group-ancestor="cascadingSettingsData.lockedByAncestor"
          :is-locked-by-application-settings="cascadingSettingsData.lockedByApplicationSetting"
          :ancestor-namespace="cascadingSettingsData.ancestorNamespace"
          class="gl-ml-1"
        />
        <template #help>
          <gl-sprintf :message="$options.i18n.alwaysOffHelpText" />
        </template>
      </gl-form-radio>
    </gl-form-radio-group>
  </div>
</template>
